import { Box, styled, useMediaQuery, useTheme } from '@mui/material';
import { ResursTrademark } from '@resurs-internal/mui';
import { ReactNode } from 'react';

import bannerFive from '../../assets/jpg/bannerFive.jpg';
import bannerFour from '../../assets/jpg/bannerFour.jpg';
import bannerOne from '../../assets/jpg/bannerOne.jpg';
import bannerThree from '../../assets/jpg/bannerThree.jpg';
import bannerTwo from '../../assets/jpg/bannerTwo.jpg';
import bannerZero from '../../assets/png/bannerZero.png';
import { useBannerTypeContext } from '../../context/BannerTypeContext';
import { useRightPanelContext } from '../../context/RightPanelContext';
import { RootBoxStyles, Wrapper } from '../../pages/RootPage/RootPage.styles';
import { CurvedBackgroundImage } from '../CurvedBackgroundImage/CurvedBackgroundImage';

const SidePanelColumn = styled(Box)({
    width: '50vw',
    minWidth: '50vw',
    boxSizing: 'border-box',
    padding: '24px 20px',
});

type Props = {
    children: ReactNode;
};

const mobileStyles = {
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingBottom: '150px',
    paddingTop: '24px',
    width: '100%',
    boxSizing: 'border-box' as const,
};

const ApplicationStyleWrapper = ({ children }: Props) => {
    const theme = useTheme();
    const desktopStyles = RootBoxStyles(theme);
    const greaterThanMid = useMediaQuery(theme.breakpoints.up('md'));
    const isMidRange = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    const { bannerType } = useBannerTypeContext();

    // Banners are intentionally desktop-only.
    // They are not rendered on mobile/tablet (down('md')) to avoid visual clutter and maintain UX consistency.
    // On mid-range screens (900–1200px, e.g. iPad Pro), only bannerZero and bannerOne are shown
    // as the other landscape images look too zoomed in at that container size.

    let selectedBanner;

    switch (bannerType) {
        case 'bannerZero':
            selectedBanner = bannerZero;
            break;
        case 'bannerOne':
            selectedBanner = bannerOne;
            break;
        case 'bannerTwo':
            selectedBanner = bannerTwo;
            break;
        case 'bannerThree':
            selectedBanner = bannerThree;
            break;
        case 'bannerFour':
            selectedBanner = bannerFour;
            break;
        case 'bannerFive':
            selectedBanner = bannerFive;
            break;
        case 'none':
            selectedBanner = '';
            break;
        default:
            selectedBanner = '';
            break;
    }

    const landscapeBanners = ['bannerTwo', 'bannerThree', 'bannerFour', 'bannerFive'];
    const effectiveBanner = isMidRange && landscapeBanners.includes(bannerType) ? bannerOne : selectedBanner;
    const greaterThanLg = useMediaQuery(theme.breakpoints.up('lg'));
    const { rightPanelContent } = useRightPanelContext();
    const hasRightPanel = greaterThanLg && rightPanelContent != null;

    const showBanner = greaterThanMid && Boolean(effectiveBanner) && !hasRightPanel;
    const hasBanner = showBanner;

    return (
        <>
            {showBanner && <CurvedBackgroundImage image={effectiveBanner!} />}

            <Wrapper greaterThanMid={greaterThanMid} hasBanner={hasBanner} hasRightPanel={hasRightPanel}>
                <Box
                    key="content"
                    data-testid="content"
                    sx={{
                        ...(greaterThanMid ? desktopStyles : mobileStyles),
                        ...(hasRightPanel && {
                            margin: '0 48px 0 auto',
                            width: '50vw',
                            minWidth: 'auto',
                            boxSizing: 'border-box',
                            padding: '48px 20px 80px 20px',
                        }),
                    }}>
                    <Box>{children}</Box>
                    {greaterThanMid && !hasRightPanel && <ResursTrademark />}
                </Box>
                {hasRightPanel && (
                    <SidePanelColumn key="sidepanel">
                        <Box sx={{ maxWidth: '500px', margin: '0 auto 0 48px', paddingTop: '48px' }}>
                            {rightPanelContent}
                        </Box>
                    </SidePanelColumn>
                )}
            </Wrapper>
            {hasRightPanel && (
                <Box
                    sx={(theme) => ({
                        textAlign: 'center',
                        padding: '24px 0 48px 0',
                        backgroundColor: theme.palette.grey[50],
                    })}>
                    <ResursTrademark />
                </Box>
            )}
        </>
    );
};

export default ApplicationStyleWrapper;
